// Types and calls per server/API.md. The site talks only to it.

import type { ClientsManifest } from "./lib";
import { parseClients } from "./lib";

export interface Me {
  id: string;
  provider: "google" | "meta";
  email: string | null;
  name: string | null;
  timezone: string;
}

export interface Conn {
  configured: boolean;
  connected: boolean;
  account_label: string | null;
  scopes: string[];
  expires_at: string | null;
  updated_at: string | null;
  sources: string[];
}

export interface Connections {
  google: Conn & { needs_reauthorization?: string[] };
  meta: Conn & { needs_reauthorization?: string[]; optional_scopes_missing?: string[] };
  tiktok: Conn;
  /** Google Play (ADR-0057): its own Google connection with the Play permissions. */
  google_play: Conn & { needs_reauthorization?: string[] };
  /** Apple Ads (ADR-0065): the grant of an Apple Ads Account Admin or Campaign Group Manager, signed in with Apple. */
  apple_ads: Conn;
}

export interface Providers {
  google: { configured: boolean };
  meta: { configured: boolean };
}

export interface ConsentRequest {
  id: string;
  client_name: string;
  redirect_uri: string;
  redirect_host: string;
  /** Хост client_id клиента по документу метаданных (ADR-0061); у DCR-клиента null. */
  client_id_host?: string | null;
  loopback: boolean;
  resource: string | null;
  scopes: string[];
  expires_at: string;
}

export interface Device {
  id: string;
  client_name: string;
  created_at: string;
  last_used_at: string | null;
  kind: "connector" | "gpt" | "extension";
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    public detail?: string,
    public reauthorize?: boolean,
    public missingScopes?: string[],
  ) {
    super(code);
  }
}

/** Called on 401: go to the home page. */
export function goHome(): void {
  window.location.assign("/?error=not_signed_in");
}

export async function call<T>(
  path: string,
  init: { method?: string; body?: unknown; redirectOn401?: boolean } = {},
): Promise<T> {
  const res = await fetch(path, {
    method: init.method ?? "GET",
    credentials: "same-origin",
    // A FormData body is sent as multipart: the browser sets the Content-Type with the boundary itself.
    headers:
      init.body === undefined || init.body instanceof FormData
        ? { Accept: "application/json" }
        : { "Content-Type": "application/json", Accept: "application/json" },
    body: init.body === undefined ? undefined : init.body instanceof FormData ? init.body : JSON.stringify(init.body),
  });
  if (res.status === 401 && init.redirectOn401 !== false) {
    goHome();
    throw new ApiError(401, "not_signed_in");
  }
  if (!res.ok) {
    let code = `http_${res.status}`;
    let detail: string | undefined;
    let reauthorize: boolean | undefined;
    let missingScopes: string[] | undefined;
    try {
      const j = (await res.json()) as {
        error?: string;
        message?: string;
        explanation?: string;
        reauthorize?: boolean;
        missing_scopes?: string[];
      };
      if (j.error) code = j.error;
      detail = j.message || j.explanation;
      if (j.reauthorize === true) reauthorize = true;
      if (Array.isArray(j.missing_scopes)) missingScopes = j.missing_scopes;
    } catch {
      /* body is not JSON */
    }
    throw new ApiError(res.status, code, detail, reauthorize, missingScopes);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** The profile, or null if not signed in: GET /api/v1/session answers 200 for guests too (ADR-0050). */
export async function getMe(): Promise<Me | null> {
  const { me } = await call<{ me: Me | null }>("/api/v1/session", { redirectOn401: false });
  return me ?? null;
}

export type PreviewSource =
  | "meta"
  | "fb_pages"
  | "instagram"
  | "tiktok"
  | "apple_ads"
  | "ga4"
  | "google_ads"
  | "search_console"
  | "app_store_connect"
  | "appsflyer"
  | "revenuecat"
  | "google_play"
  | "apple";

/** Read-only data call under the site session (server/API.md, "Data preview"). */
export const previewCall = <T = unknown>(source: PreviewSource, mode: string, args: unknown = {}) =>
  call<T>(`/api/v1/preview/${source}/${mode}`, { method: "POST", body: previewClient ? { ...(args as object), client: previewClient } : args });

/** The client the Data preview reads for (ADR-0062): set by the page once the account has several; none - the default client. */
let previewClient: string | undefined;
export const setPreviewClient = (slug: string | undefined): void => {
  previewClient = slug;
};

/** One message of the feedback list for the owner (ADR-0041); 404 for everyone else. */
export interface FeedbackItem {
  id: number;
  created_at: string;
  email: string | null;
  name: string | null;
  channel: "chat" | "site";
  client: string | null;
  kind: "bug" | "idea" | "other";
  text: string;
  context: Record<string, unknown> | null;
  attachments: { id: number; mime: string; byte_size: number }[];
  /** ADR-0049: the thread; absent in old responses. */
  replies?: FeedbackReply[];
  status?: FeedbackThreadStatus;
}
export type FeedbackThreadStatus = "needs_answer" | "answered" | "delivered";
export interface FeedbackReply {
  id: number;
  author: "developer" | "user";
  text: string;
  created_at: string;
  delivered_at: string | null;
  delivered_via: string | null;
}
export const getAdminFeedback = () =>
  call<{ items: FeedbackItem[] }>("/api/v1/admin/feedback?status=all", { redirectOn401: false });
export const sendAdminReply = (id: number, text: string) =>
  call<{ id: number; feedback_id: number; created_at: string }>(`/api/v1/admin/feedback/${id}/replies`, {
    method: "POST",
    body: { text },
    redirectOn401: false,
  });
export const deleteAdminReply = (id: number, replyId: number) =>
  call<{ deleted: true }>(`/api/v1/admin/feedback/${id}/replies/${replyId}`, { method: "DELETE", redirectOn401: false });

/** Feedback form of the site (ADR-0041): `null` when the feature is off (404) or unreachable. */
export interface FeedbackInfo {
  enabled: boolean;
  max_text: number;
  max_image_bytes: number;
}
export async function getFeedbackInfo(): Promise<FeedbackInfo | null> {
  try {
    const r = await call<FeedbackInfo>("/api/v1/feedback/settings", { redirectOn401: false });
    return r?.enabled ? r : null;
  } catch {
    return null;
  }
}
/** The message as multipart (session cookie; the browser adds the site's Origin). */
export function sendFeedback(text: string, file: File | null) {
  const form = new FormData();
  form.append("text", text);
  form.append("kind", "other");
  if (file) form.append("file", file);
  return call<{ id: number; received_at: string; add_screenshot_url: string }>("/api/v1/feedback", { method: "POST", body: form, redirectOn401: false });
}
/** A message with a kind and a context, as JSON (the «more clients» form of ADR-0062: no picture). */
export const sendFeedbackJson = (text: string, kind: "bug" | "idea" | "other", context?: Record<string, string>) =>
  call<{ id: number; received_at: string; add_screenshot_url: string }>("/api/v1/feedback", {
    method: "POST",
    body: context ? { text, kind, context } : { text, kind },
    redirectOn401: false,
  });
export function attachFeedbackScreenshot(id: number, file: File) {
  const form = new FormData();
  form.append("file", file);
  return call<{ id: number }>(`/api/v1/feedback/${id}/screenshot`, { method: "POST", body: form, redirectOn401: false });
}


/** The user's own notes and threads (ADR-0049). `limit` makes the server answer the list, not the public form settings. */
export interface MyNote {
  id: number;
  sent_on: string;
  kind: "bug" | "idea" | "other";
  channel: string;
  excerpt: string;
  replies: number;
  last: { author: "developer" | "user"; on: string } | null;
  status: FeedbackThreadStatus;
  /** Developer replies not shown to the user yet. */
  undelivered?: number;
}
export interface MyThread {
  id: number;
  sent_on: string;
  created_at: string;
  kind: string;
  text: string;
  status: FeedbackThreadStatus;
  attachments: { id: number; mime: string; byte_size: number }[];
  replies: { id: number; author: "developer" | "user"; text: string; on: string; created_at: string; delivered: boolean }[];
}
export const listMyFeedback = () => call<{ items: MyNote[] }>("/api/v1/feedback?limit=50", { redirectOn401: false });
export const getMyFeedback = (id: number) => call<MyThread>(`/api/v1/feedback/${id}`, { redirectOn401: false });
export const replyMyFeedback = (id: number, text: string) =>
  call<{ id: number }>(`/api/v1/feedback/${id}/replies`, { method: "POST", body: { text }, redirectOn401: false });
export const markFeedbackDelivered = (id: number) =>
  call<{ delivered: number }>(`/api/v1/feedback/${id}/delivered`, { method: "POST", redirectOn401: false });
/** The plan block of server/API.md «Billing» (ADR-0045, ADR-0048). */
export type PlanStateName = "off" | "trial" | "trial-ending" | "expired" | "active" | "past-due" | "paused" | "cancelled" | "comp" | "gift";
export type BillingPeriod = "monthly" | "yearly";
export interface BillingPlan {
  state: PlanStateName;
  days_left?: number | null;
  ends_on?: string | null;
  period_end?: string | null;
  period?: BillingPeriod | null;
  price?: { currency: string; monthly: number; yearly: number; excl_vat: boolean };
  subscribe_url?: string;
  extension?: { available: boolean; days: number };
  /** past-due: when Stripe stops retrying (YYYY-MM-DD). */
  pause_on?: string;
  /** paused: where the card is updated (`/account#plan`). */
  update_card_url?: string;
  /** trial / trial-ending: the first day of the trial (YYYY-MM-DD) and its length - 30, +3 after the extension. */
  trial_started_on?: string;
  trial_days?: number;
  /** comp with an end date (BL-139) - shown when the server sends it. */
  comp_until?: string | null;
  /** gift (ADR-0059): the day the gift subscription ends (YYYY-MM-DD, the user's time zone); the same day as `ends_on`. */
  gift_until?: string | null;
  /** gift in its last 10 days (BL-185): the state is still `gift`; the Home strip and the Plan card warn. */
  gift_ending?: boolean;
}
export interface BillingState {
  plan: BillingPlan;
  has_billing_account: boolean;
  cancel_at_period_end: boolean;
  /** ADR-0058: a Stripe Managed Payments subscription (Stripe the seller); false - an older one (VALAS the seller) or none. */
  managed?: boolean;
  /** BL-177: the currency of the last payment (Adaptive Pricing - not always EUR); null - nothing paid yet. */
  last_payment_currency?: string | null;
}
export const getBillingState = () => call<BillingState>("/api/billing/state", { redirectOn401: false });
/** Stripe Checkout session (ADR-0046): the browser goes to `url`. */
export const startCheckout = (period: BillingPeriod) =>
  call<{ url: string }>("/api/billing/checkout", { method: "POST", body: { period } });

/** /admin/billing (BL-139, server/API.md «Admin → Billing»): the owner's billing console; 403/401 for everyone else. */
/** A sum in one currency (major units); the lists are empty when there is nothing. */
export interface AdminAmount {
  currency: string;
  amount: number;
}
export interface AdminBillingEvent {
  event_id: string;
  type: string;
  created: string;
  email: string | null;
  /** true - Managed Payments (Stripe is the seller); null - unknown. */
  managed: boolean | null;
  /** 'ours' - started by us; 'external' - known not to be ours (Stripe, Dashboard, the buyer); null - not known. */
  initiator: "ours" | "external" | null;
  error: string | null;
}
export interface AdminBillingSummary {
  users: number;
  states: Record<string, number>;
  mrr: { amount: number; currency: string; monthly: number; yearly: number; excl_vat: boolean };
  last_30_days: { new_subscriptions: number; cancellations: number; refunds: number; revenue: AdminAmount[]; refunded: AdminAmount[]; failed_payments: number; external_events?: number };
  /** ADR-0058: the latest journal events with the deal mode and, where known, who started them. */
  recent_events?: AdminBillingEvent[];
  /** BL-184: second live subscriptions of the last 30 days (cancelled automatically, or a manual step is left). */
  second_subscriptions?: { event_id: string; created: string; email: string | null; error: string }[];
  trials_ending_7d: { count: number; items: { id: number; email: string | null; ends_at: string; days_left: number }[] };
  /** ADR-0059: counters of gift codes (created by the console utility only): disjoint groups redeemed / revoked / expired / unused. */
  gift_codes?: { created: number; redeemed: number; revoked: number; expired: number; unused: number };
  stripe_mode: "test" | "live";
}
export interface AdminBillingUser {
  id: number;
  email: string | null;
  provider: string;
  created_at: string | null;
  last_request_at: string | null;
  state: PlanStateName;
  period: BillingPeriod | null;
  ends_on: string | null;
  days_left: number | null;
  trial_ends_at: string | null;
  period_end_at: string | null;
  /** ADR-0059: the end of the redeemed gift (kept after a purchase, for the record). */
  gift_until?: string | null;
  stripe_status: string | null;
  stripe_subscription_id: string | null;
  cancel_at_period_end: boolean;
  /** ADR-0058: the subscription was bought with Stripe Managed Payments (Stripe is the seller). */
  managed?: boolean;
  customer_id: string | null;
  customer_url: string | null;
  lists: { admin: boolean; comp: boolean; comp_until: string | null; pilot: boolean };
  /** The last paid invoice with its amount; null when there is no amount (never a payment without a sum). */
  last_payment: { at: string; amount: number; currency: string } | null;
  refunded_total: AdminAmount | null;
}
export interface AdminBillingUsers {
  items: AdminBillingUser[];
  total: number;
  page: number;
  pages: number;
  per_page: number;
}
export interface AdminBillingQuery {
  q?: string;
  state?: string;
  sort?: string;
  dir?: "asc" | "desc";
  page?: number;
}
export interface AdminListEntry {
  email: string;
  until: string | null;
  note: string | null;
  added_by: string | null;
  created_at: string | null;
  active: boolean;
}
export interface AdminList {
  kind: "comp" | "pilot";
  env: { email: string; source: string }[];
  items: AdminListEntry[];
}
/** Answer of an action: `pending` - the state changes when the Stripe webhook lands. */
export interface AdminActionResult {
  ok: true;
  pending: boolean;
  refund?: { id: string; amount: number; currency: string; status: string | null };
  comp_until?: string;
  subscriber_hint?: string;
  dashboard_url?: string | null;
  trial_ends_at?: string;
}
export const adminBillingQueryString = (q: AdminBillingQuery): string => {
  const p = new URLSearchParams();
  if (q.q) p.set("q", q.q);
  if (q.state) p.set("state", q.state);
  if (q.sort) p.set("sort", q.sort);
  if (q.dir) p.set("dir", q.dir);
  if (q.page && q.page > 1) p.set("page", String(q.page));
  const s = p.toString();
  return s ? `?${s}` : "";
};
const adminBilling = "/api/admin/billing";
export const getAdminBillingSummary = () => call<AdminBillingSummary>(`${adminBilling}/summary`, { redirectOn401: false });
export const getAdminBillingUsers = (q: AdminBillingQuery) =>
  call<AdminBillingUsers>(`${adminBilling}/users${adminBillingQueryString(q)}`, { redirectOn401: false });
export const adminBillingCsvUrl = (q: AdminBillingQuery) => `${adminBilling}/users.csv${adminBillingQueryString({ ...q, page: 1 })}`;
export const adminBillingAction = (userId: number, action: "refund" | "cancel" | "trial" | "extend", body: object) =>
  call<AdminActionResult>(`${adminBilling}/users/${userId}/${action}`, { method: "POST", body, redirectOn401: false });
export const getAdminBillingList = (kind: "comp" | "pilot") => call<AdminList>(`${adminBilling}/lists/${kind}`, { redirectOn401: false });
export const addAdminBillingListEntry = (kind: "comp" | "pilot", body: { email: string; until?: string | null; note?: string | null }) =>
  call<{ ok: true }>(`${adminBilling}/lists/${kind}`, { method: "POST", body, redirectOn401: false });
export const removeAdminBillingListEntry = (kind: "comp" | "pilot", email: string) =>
  call<{ ok: true }>(`${adminBilling}/lists/${kind}?email=${encodeURIComponent(email)}`, { method: "DELETE", redirectOn401: false });

export const getProviders = () => call<Providers>("/api/v1/auth/providers", { redirectOn401: false });
/** `?client=<slug>` for a call about one client (ADR-0062); none - the server's default client. */
export const clientQuery = (client?: string): string => (client ? `?client=${encodeURIComponent(client)}` : "");

export const getConnections = (client?: string) => call<Connections>(`/api/v1/connections${clientQuery(client)}`);
export const disconnect = (p: "google" | "meta" | "tiktok" | "google_play" | "apple_ads", client?: string) =>
  call<void>(`/auth/${p}/disconnect${clientQuery(client)}`, { method: "POST" });

/** A client: a named group of connections and keys (ADR-0062, server/API.md «Clients»). */
export interface SiteClient {
  id: number;
  slug: string;
  name: string;
  sources: string[];
  created_at?: string;
}
export interface SiteClients {
  clients: SiteClient[];
  max: number;
}
export const getSiteClients = () => call<SiteClients>("/api/v1/clients", { redirectOn401: false });
export const createSiteClient = (name: string) => call<SiteClient>("/api/v1/clients", { method: "POST", body: { name } });
export const renameSiteClient = (id: number, name: string) =>
  call<SiteClient>(`/api/v1/clients/${id}`, { method: "PATCH", body: { name } });
export const deleteSiteClient = (id: number) => call<void>(`/api/v1/clients/${id}`, { method: "DELETE" });
export const patchTimezone = (timezone: string) =>
  call<Me>("/api/v1/me", { method: "PATCH", body: { timezone } });
export const deleteMe = () => call<void>("/api/v1/me", { method: "DELETE" });
export const logout = () => call<void>("/auth/logout", { method: "POST", redirectOn401: false });
export const getDevices = () => call<{ items: Device[] }>("/api/oauth/connections");
export const deleteDevice = (id: string) =>
  call<{ ok: true }>(`/api/oauth/connections/${encodeURIComponent(id)}`, { method: "DELETE" });
export const getConsentRequest = (id: string) =>
  call<{ request: ConsentRequest }>(`/api/oauth/requests/${encodeURIComponent(id)}`, {
    redirectOn401: false,
  });
export const decideConsent = (id: string, approve: boolean) =>
  call<{ redirect_to: string }>(`/api/oauth/requests/${encodeURIComponent(id)}`, {
    method: "POST",
    body: { approve },
  });

export type SecretKind =
  | "appsflyer_token"
  | "revenuecat_key"
  | "google_ads_login_customer_id"
  | "app_store_connect_key"
  | "app_store_connect_vendor_number"
  | "google_play_bucket";

/** The App Store Connect team key: sent as one object, never stored in the browser. */
export interface AscKeyValue {
  issuer_id: string;
  key_id: string;
  private_key: string;
}
export interface SecretInfo {
  set: boolean;
  hint: string | null;
  updated_at: string | null;
}
export type SecretsMap = Partial<Record<SecretKind, SecretInfo>>;

/** Hand-entered source keys saved on the server (server/API.md, "/api/v1/secrets"). */
export const getSecrets = (client?: string) => call<{ items: SecretsMap }>(`/api/v1/secrets${clientQuery(client)}`);
export const putSecret = (kind: SecretKind, value: string | AscKeyValue, client?: string) =>
  call<{ kind: SecretKind; set: true; hint: string | null; updated_at: string }>(
    `/api/v1/secrets/${encodeURIComponent(kind)}`,
    { method: "PUT", body: client ? { value, client } : { value } },
  );
export const deleteSecret = (kind: SecretKind, client?: string) =>
  call<{ ok: true }>(`/api/v1/secrets/${encodeURIComponent(kind)}${clientQuery(client)}`, { method: "DELETE" });

/** clients.json sits next to the client files; no file gives null. */
export async function getClients(): Promise<ClientsManifest | null> {
  const res = await fetch("/clients/clients.json", { credentials: "same-origin" });
  if (!res.ok) return null;
  try {
    return parseClients(await res.json());
  } catch {
    return null;
  }
}
